import React from "react";
import { formatToDateFull } from "../master/FormatToDateTime";
import {
  buildMarksheet,
  getRemark,
  nextClass,
  getSummaryItems,
  FILL_IN_ITEMS,
} from "./marksheetUtils";
import {
  useSetting,
  getLogo,
  schoolAddress,
  schoolContact,
  getStudentFields,
  MarksheetStyle,
  MarksTable,
  CoScholastic,
  GradeLegend,
} from "./marksheetShared";

// Marksheet 4: Sidebar layout. Tinted teal column holds the school and the
// student profile; marks and results sit in the main column.

const CSS = `
& { padding: 0; }
& .ms4-wrap { display: grid; grid-template-columns: 62mm 1fr; min-height: 288mm; }

& .ms4-side { display: flex; flex-direction: column; gap: 12px; padding: 10mm 6mm 8mm; background: rgba(13,148,136,.14); }
& .ms4-brand { text-align: center; }
& .ms4-logo { width: 70px; height: 70px; object-fit: contain; margin-bottom: 6px; }
& .ms4-name { font-size: 14px; font-weight: 800; line-height: 1.2; }
& .ms4-meta { margin-top: 2px; font-size: 9px; opacity: .75; }
& .ms4-profile { display: flex; flex-direction: column; gap: 7px; margin-top: 6px; }
& .ms4-field span { display: block; font-size: 8.5px; opacity: .65; }
& .ms4-field b { font-size: 11px; font-weight: 700; overflow-wrap: anywhere; }
& .ms4-side .ms-legend { flex-direction: column; flex-wrap: nowrap; gap: 1px; margin-top: auto; }

& .ms4-main { padding: 10mm 8mm 8mm; }
& .ms4-title { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 10px; }
& .ms4-title-main { font-size: 22px; font-weight: 800; }
& .ms4-title-sub { font-size: 10px; opacity: .75; }

& .ms-marks th { padding: 6px 4px; text-align: center; font-weight: 800; background: rgba(13,148,136,.2); }
& .ms-marks td { padding: 5px 4px; text-align: center; border-bottom: .5pt solid rgba(0,0,0,.3) !important; }
& .ms-marks tfoot td { font-weight: 800; background: rgba(13,148,136,.1); border-bottom: 0 !important; }

& .ms4-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin: 12px 0; }
& .ms4-tile { min-height: 40px; padding: 6px 8px; background: rgba(13,148,136,.1); border-left: 3px solid rgba(13,148,136,.9) !important; }
& .ms4-tile span { display: block; font-size: 9px; opacity: .7; }
& .ms4-tile b { font-size: 14px; font-weight: 800; }

& .ms-cos th { padding: 5px 6px; text-align: center; font-weight: 800; background: rgba(13,148,136,.2); }
& .ms-cos td { padding: 6px; text-align: center; border-bottom: .5pt solid rgba(0,0,0,.3) !important; }
& .ms-cos th:first-child, & .ms-cos td:first-child { text-align: left; }

& .ms4-notes { display: grid; grid-template-columns: 1.6fr 1fr; gap: 6px 14px; margin-top: 12px; }
& .ms4-note span { display: block; font-size: 9px; opacity: .65; }
& .ms4-note p { min-height: 20px; font-weight: 600; border-bottom: .5pt solid rgba(0,0,0,.3) !important; }
& .ms4-note--wide { grid-column: 1 / -1; }

& .ms4-sign { display: flex; justify-content: space-between; gap: 20px; margin-top: 30px; }
& .ms4-sign div { flex: 1; text-align: center; padding-top: 4px; border-top: 1px solid #000 !important; }
`;

export default function StudentMarksheet4({ data, batch, examDate }) {
  const setting = useSetting();
  const sheet = buildMarksheet(data, batch);
  const logo = getLogo(setting);
  const fields = getStudentFields(data, batch);
  const tiles = [
    ...getSummaryItems(sheet.overall),
    ...FILL_IN_ITEMS.map((label) => ({ label, value: "" })),
  ];
  const remark = getRemark(sheet.overall.percentage, data?.student_id);

  return (
    <>
      <MarksheetStyle setting={setting} scope="ms4" css={CSS} />

      <div className="a4-page-marksheet ms4">
        <div className="ms4-wrap">
          <div className="ms4-side">
            <div className="ms4-brand">
              {logo && <img className="ms4-logo" src={logo} alt="" />}
              <div className="ms4-name">{setting?.name}</div>
              <div className="ms4-meta">{schoolAddress(setting)}</div>
              <div className="ms4-meta">{schoolContact(setting)}</div>
            </div>

            <div className="ms4-profile">
              {fields.map((f) => (
                <div className="ms4-field" key={f.label}>
                  <span>{f.label}</span>
                  <b>{f.value || "-"}</b>
                </div>
              ))}
            </div>

            <GradeLegend />
          </div>

          <div className="ms4-main">
            <div className="ms4-title">
              <span className="ms4-title-main">Report Card</span>
              <span className="ms4-title-sub">
                Session {batch?.sessions?.session}
              </span>
            </div>

            <MarksTable {...sheet} />

            <div className="ms4-tiles">
              {tiles.map((t) => (
                <div className="ms4-tile" key={t.label}>
                  <span>{t.label}</span>
                  <b>{t.value}</b>
                </div>
              ))}
            </div>

            <CoScholastic />

            <div className="ms4-notes">
              <div className="ms4-note ms4-note--wide">
                <span>Class teacher's remark</span>
                <p>{remark}</p>
              </div>
              <div className="ms4-note">
                <span>Promoted to class</span>
                <p>{nextClass(batch?.course?.title)}</p>
              </div>
              <div className="ms4-note">
                <span>Date</span>
                <p>{formatToDateFull(examDate)}</p>
              </div>
            </div>

            <div className="ms4-sign">
              <div>Class Teacher</div>
              <div>Principal</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
